import type { SmartWorkspaceDTO } from "@eu.mdotm.api/smart-workspaces";
import type {
	ParameterizedConceptDTOWithMandatoryUuid,
	Workspace,
	WorkspaceColumnOrigin,
	WorkspaceColumnScope,
} from "../ctx/context";

/**
 * The workspace as **one value**.
 *
 * The same information used to live in ten atoms, reassembled on every read and every write. The split
 * bought nothing at the boundary that matters; what it cost is that "who may write what" was answered
 * per atom instead of once. So the store is one value and every write is a `dispatch`.
 *
 * The slices are drawn along the line that is actually load-bearing — **what reaches the wire**:
 *
 * - `meta` is the document as ingested. It is spread back out by the serializer, so fields this client
 *   does not model (`richAcl`, `status`, `taxonomy`, …) round-trip untouched.
 * - `concepts` is one provenance-tagged pool. Merging the two halves is a *projection*, not a copy:
 *   they persist differently, so the tag has to survive (see {@link ConceptPool}).
 * - `content` is everything the workspace PUT carries.
 * - `ui` is everything it must **not** carry. `pageSize` is content because the DTO has a field for it;
 *   `page` and `resultTotal` are not, and `ctx/adapter.test.ts` pins that they come back as `1` / `0`.
 *   That is the whole reason the two are separate slices: an ephemeral field cannot reach the PUT by
 *   accident because the serializer is never handed one.
 */
export type WorkspaceState = {
	meta: WorkspaceMeta;
	concepts: ConceptPool;
	content: WorkspaceContent;
	ui: WorkspaceUi;
};

export type WorkspaceMeta = {
	/**
	 * The document this state was ingested from, carried whole. `contextToSmartWorkspace` spreads it
	 * first and overwrites the fields the client owns, so anything the client does not model survives a
	 * round trip. Absent for a workspace that does not exist upstream yet (the default one).
	 */
	document?: SmartWorkspaceDTO;
};

/** Everything the whole-document PUT carries. Field types are taken from `Workspace`, never restated. */
export type WorkspaceContent = {
	columns: Workspace["columns"];
	charts: Workspace["charts"];
	filters: Workspace["filters"];
	sort: Workspace["sort"];
	view: Workspace["view"];
	pageSize: Workspace["pagination"]["size"];
};

/** Everything that must never reach the PUT. */
export type WorkspaceUi = {
	search: Workspace["search"];
	selection: Workspace["selection"];
	page: Workspace["pagination"]["page"];
	/** Row count reported by the last data response — server-derived, not user state. */
	resultTotal: Workspace["pagination"]["total"];
};

/**
 * Where a concept persists, and therefore who is allowed to delete it.
 *
 * Aliases of the vocabulary already declared in `ctx/context.tsx` rather than new names, so the two
 * cannot drift:
 *
 * - `WORKSPACE` — private. Rides the workspace PUT, is swept when nothing references it.
 * - `STANDARD` — shared. Has its own API, is visible to other users of the tenant, and **outlives the
 *   workspace**, so the sweep must never touch it.
 */
export type ConceptOrigin = WorkspaceColumnOrigin;

/**
 * Reach of a shared concept. Not populated by any ingestion path today — the shared-concept API does
 * not report it — and kept only so that the pool has the field when it does; nothing branches on it.
 */
export type ConceptScope = WorkspaceColumnScope;

export type PooledConcept = {
	concept: ParameterizedConceptDTOWithMandatoryUuid;
	origin: ConceptOrigin;
	scope?: ConceptScope;
};

/**
 * One pool, ordered, provenance-tagged.
 *
 * **The order is part of the contract**, which is why this is a list and not a map: the private half in
 * pool order becomes `SmartWorkspaceDTO.parameterizedConcepts`, and the merged order decides
 * `grid.projection` — which in turn decides column order for a workspace that has no stored column
 * layout yet. Two rules keep both stable:
 *
 * 1. every `WORKSPACE` entry precedes every `STANDARD` one, so "private then shared" (today's
 *    `allParametrizedConcepts$`) is just the pool read front to back;
 * 2. insertion is stable within an origin — {@link addPrivateConcept} appends *after the last private
 *    entry*, not at the end of the pool.
 *
 * A uuid may legitimately appear twice, once per origin — today's two atoms allow it — and
 * {@link resolveConcepts} settles it the way `allParametrizedConcepts$` does: first position wins,
 * last value wins.
 */
export type ConceptPool = PooledConcept[];

// --- Pool operations -----------------------------------------------------------------------------

/** The pool read front to back, deduplicated by uuid, exactly as `allParametrizedConcepts$` does it. */
export function resolveConcepts(pool: ConceptPool): ParameterizedConceptDTOWithMandatoryUuid[] {
	return [...new Map(pool.map((entry) => [entry.concept.uuid, entry.concept])).values()];
}

export function findConcept(pool: ConceptPool, uuid: string): PooledConcept | undefined {
	return pool.find((entry) => entry.concept.uuid === uuid);
}

export function privateConcepts(pool: ConceptPool): ParameterizedConceptDTOWithMandatoryUuid[] {
	return pool.flatMap((entry) => (entry.origin === "WORKSPACE" ? [entry.concept] : []));
}

export function sharedConcepts(pool: ConceptPool): ParameterizedConceptDTOWithMandatoryUuid[] {
	return pool.flatMap((entry) => (entry.origin === "STANDARD" ? [entry.concept] : []));
}

/** Appends a private concept after the last private entry — see rule 2 on {@link ConceptPool}. */
export function addPrivateConcept(pool: ConceptPool, concept: ParameterizedConceptDTOWithMandatoryUuid): ConceptPool {
	const firstShared = pool.findIndex((entry) => entry.origin === "STANDARD");
	const entry: PooledConcept = { concept, origin: "WORKSPACE" };
	if (firstShared === -1) {
		return [...pool, entry];
	}
	return [...pool.slice(0, firstShared), entry, ...pool.slice(firstShared)];
}

export function addSharedConcept(
	pool: ConceptPool,
	concept: ParameterizedConceptDTOWithMandatoryUuid,
	scope?: ConceptScope,
): ConceptPool {
	return [...pool, { concept, origin: "STANDARD", ...(scope ? { scope } : {}) }];
}

/**
 * Patches a **private** concept in place, leaving the pool order alone. A no-op when the uuid names a
 * shared concept or nothing at all — `updateParametrizedConcept` behaves the same way today, and
 * `parametrized-concept.test.ts` pins both.
 */
export function patchPrivateConcept(
	pool: ConceptPool,
	uuid: string,
	patch: Omit<Partial<ParameterizedConceptDTOWithMandatoryUuid>, "uuid">,
): ConceptPool {
	const index = pool.findIndex((entry) => entry.origin === "WORKSPACE" && entry.concept.uuid === uuid);
	if (index === -1) {
		return pool;
	}
	const next = pool.slice();
	next[index] = { ...next[index], concept: { ...next[index].concept, ...patch } };
	return next;
}

export function removeConcept(pool: ConceptPool, uuid: string): ConceptPool {
	return pool.filter((entry) => entry.concept.uuid !== uuid);
}

// --- Bridge to today's ten-atom `Workspace` -------------------------------------------------------

/**
 * `Workspace` → `WorkspaceState`.
 *
 * The rest-and-assert below is the guard: destructuring every field and then requiring the remainder to
 * be empty turns "a field of `Workspace` has no home in `WorkspaceState`" into a compile error, which is
 * the same trick `satisfies Record<keyof T, unknown>` plays on the field lists that stay restated.
 */
export function fromWorkspace(workspace: Workspace): WorkspaceState {
	const {
		view,
		search,
		filters,
		columns,
		charts,
		privateParametrizedConcepts,
		sort,
		pagination,
		selection,
		workspace: document,
		sharedParametrizedConcepts,
		...unaccountedFor
	} = workspace;
	const _everyFieldHasAHome: Record<string, never> = unaccountedFor;

	return {
		meta: { document },
		concepts: [
			...privateParametrizedConcepts.map((concept): PooledConcept => ({ concept, origin: "WORKSPACE" })),
			...sharedParametrizedConcepts.map((concept): PooledConcept => ({ concept, origin: "STANDARD" })),
		],
		content: { columns, charts, filters, sort, view, pageSize: pagination.size },
		ui: { search, selection, page: pagination.page, resultTotal: pagination.total },
	};
}

/** `WorkspaceState` → `Workspace`. The inverse of {@link fromWorkspace}, and lossless. */
export function toWorkspace(state: WorkspaceState): Workspace {
	return {
		view: state.content.view,
		search: state.ui.search,
		filters: state.content.filters,
		columns: state.content.columns,
		charts: state.content.charts,
		privateParametrizedConcepts: privateConcepts(state.concepts),
		sort: state.content.sort,
		pagination: { page: state.ui.page, size: state.content.pageSize, total: state.ui.resultTotal },
		selection: state.ui.selection,
		workspace: state.meta.document,
		sharedParametrizedConcepts: sharedConcepts(state.concepts),
	};
}
